import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでシミュレータのシミュレーションが成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface ICreateSimulatorRequestModel {
  /**
   * 推定用入出力データファイルのファイルID
   */
  trainfile: string;
  /**
   * 評価用入出力データファイルのファイルID
   */
  validationfile: string;
  /**
   * シミュレータ名（最大50文字）
   */
  name: string;
  /**
   * システム同定実施時に参照する設定の種類（Auto/Custom）
   */
  type: string;
}

function AxiosPost(
  url: string,
  params: ICreateSimulatorRequestModel,
  token?: string
) {
  if (token) {
    return axios.post(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface ICreateSimulatorParams
  extends ICreateSimulatorRequestModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
}

/**
 * シミュレータを作成します
 * @function createSimulator
 * @param {ICreateSimulatorParams} params - シミュレータ作成パラメータ
 */
export function createSimulator(params: ICreateSimulatorParams): void {
  const { 
    authService, 
    projectid, 
    trainfile,
    validationfile,
    name,
    type
  } = params;
  const req: ICreateSimulatorRequestModel = {
    trainfile: trainfile,
    validationfile: validationfile,
    name: name,
    type: type
  };
  const url = WebAPI.CREATE_SIMULATOR(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              createSimulatorRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * シミュレータを作成します
 * @function createSimulatorRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICreateSimulatorParams} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function createSimulatorRetry401(
  authService: AuthService,
  url: string,
  req: ICreateSimulatorRequestModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}